import type { DocPageContent } from '../docs-content'

const page = {
  order: 6,
  slug: 'timeline',
  title: '时间轴基础编辑',
  description:
    '轨道、添加片段、选择、分割与合并、删除、吸附、标记、范围、序列和缩放。',
  category: '核心编辑',
  related: ['editing-tools', 'preview', 'media', 'export', 'keyboard-shortcuts'],
  sections: [
    {
      title: '轨道',
      blocks: [
        {
          kind: 'list',
          items: [
            '**视频轨道**容纳可视项：视频、图像、文本、形状、字幕和调整图层。',
            '**音频轨道**容纳音频片段以及与视频文件链接的音频。',
            '需要更多空间时，使用**添加视频轨道**或**添加音频轨道**。',
            '轨道控件包括**锁定**、**独奏**、**启用/禁用**（可见性）和同步锁定。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '轨道组只有一层深度，并充当标题——静音、可见性和锁定会从组向下传递到其轨道。',
        },
      ],
    },
    {
      title: '添加并排列片段',
      blocks: [
        {
          kind: 'list',
          items: [
            '将片段从媒体面板拖到视频或音频轨道上。',
            '使用**选择**工具（`V`）移动和重新排列片段。',
            '**链接选择**会使视频片段及其音频一起移动；取消链接后可单独编辑其中一个。',
            '用 `Shift+Arrow`（1 像素）或 `Ctrl+Shift+Arrow`（10 像素）微移所选项目。',
          ],
        },
      ],
    },
    {
      title: '分割、合并与删除',
      blocks: [
        {
          kind: 'list',
          items: [
            '用 `Alt+C` 在播放头处分割，或使用**剃刀**工具（`C`）在点击处切割。',
            '用 `Shift+J` 合并同一片段的相邻部分。',
            '**删除**会留下空隙；**波纹删除**（`Ctrl+Delete`）会移除片段并闭合空隙。',
            '使用**闭合所有空隙**将片段收拢并移除轨道上的空白。',
          ],
        },
      ],
    },
    {
      title: '吸附、标记与范围',
      blocks: [
        {
          kind: 'list',
          items: [
            '用 `S` 切换吸附，使片段吸附到附近的切口、标记和播放头。',
            '用 `M` 添加标记用于备注和计时参考；用 `[` 和 `]` 在标记之间跳转。',
            '设置入点（`I`）和出点（`O`）以定义用于预览和导出的范围；用 `Alt+X` 清除它们。',
          ],
        },
      ],
    },
    {
      title: '序列（多时间轴）',
      blocks: [
        {
          kind: 'paragraph',
          text: '**序列**是一个自包含的时间轴，拥有自己的轨道、片段、标记和入/出范围。时间轴上方的标签栏会列出**主时间轴**以及每一个序列——点击标签页即可切换正在编辑的那个。',
        },
        {
          kind: 'list',
          items: [
            '用标签栏末尾的 **+** 按钮创建序列；它会作为独立标签页打开。',
            '点击标签页切换序列；**主**标签页始终排在第一位。',
            '双击标签页可**重命名**它，或使用标签页选项菜单（⋮）进行**重命名**、**关闭标签页**和**删除序列**。',
            '**关闭标签页**仅移除该标签页——序列仍可在媒体面板以及任何引用它的片段中使用。**删除序列**会将其完全移除（可撤销），同时移除任何引用它的片段。',
            '**复合片段**可作为序列标签页打开——在媒体面板中，右键点击复合片段并选择**作为标签页打开**，将其作为独立时间轴编辑。',
            '将序列拖到时间轴上，可将其作为片段嵌套（标记为**序列**）；双击该片段可跳转到其标签页。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '每个序列都保留自己的标记、入/出范围、视图和撤销历史——切换标签页绝不会混淆它们。',
        },
      ],
    },
    {
      title: '缩放与撤销',
      blocks: [
        {
          kind: 'list',
          items: [
            '用 `Ctrl+=` 和 `Ctrl+-` 缩放，用 `\\` 适配整个剪辑，用 `Shift+\\` 回到 100%。',
            '缩放只改变视图，永远不会改变片段时序。',
            '用 `Ctrl+Z` 撤销，`Ctrl+Shift+Z` 重做；撤销历史深度可在设置中配置。',
          ],
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
